import React, { useState, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

export default function QCCheckScanner() {
  const [barcode, setBarcode] = useState('');
  const [lotDetails, setLotDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Failure Form State
  const [showFailForm, setShowFailForm] = useState(false);
  const [defectCategory, setDefectCategory] = useState('');
  const [actualQty, setActualQty] = useState('');
  const [comments, setComments] = useState('');
  const [photos, setPhotos] = useState([]);

  const fileInputRef = useRef(null);

  const handleScan = async (e) => {
    e.preventDefault();
    if (!barcode.trim()) return;

    setLoading(true);
    setLotDetails(null);
    setShowFailForm(false);
    
    try {
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/qc/lot/${barcode.trim()}`, { withCredentials: true });
      if (res.data.success) {
        setLotDetails(res.data.data);
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to fetch lot details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const submitQC = async (status) => {
    if (status === 'FAIL') {
      if (!defectCategory) return Swal.fire('Validation Error', 'Please select a defect category.', 'warning');
      if (!actualQty) return Swal.fire('Validation Error', 'Please enter actual checked quantity.', 'warning');
      if (photos.length === 0) return Swal.fire('Validation Error', 'Please upload at least one evidence photo for defects.', 'warning');
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('lot_id', lotDetails.lot_id);
      formData.append('process_id', lotDetails.process_id);
      formData.append('status', status);
      // In production, get actual logged-in user Emp ID
      formData.append('checked_by', 'QC_Inspector'); 

      if (status === 'FAIL') {
        formData.append('defect_category', defectCategory);
        formData.append('expected_qty', lotDetails.expected_qty || 0);
        formData.append('actual_qty', actualQty);
        formData.append('comments', comments);
        photos.forEach(file => {
          formData.append('evidence_photos', file);
        });
      }

      const res = await axios.post(`${BASE_URL}/api/wip-dashboard/qc/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      if (res.data.success) {
        Swal.fire('Success', res.data.message, 'success');
        resetScanner();
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Submission failed.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetScanner = () => {
    setBarcode('');
    setLotDetails(null);
    setShowFailForm(false);
    setDefectCategory('');
    setActualQty('');
    setComments('');
    setPhotos([]);
  };

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      {/* HEADER */}
      <div style={{ background: "linear-gradient(135deg, #4338ca 0%, #312e81 100%)", padding: "24px", color: "white", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}>
        <h3 style={{ margin: 0, fontWeight: 700, fontSize: "20px", display: "flex", alignItems: "center", gap: 10 }}>
          <i className="bi bi-shield-check" style={{ fontSize: "24px", color: "#818cf8" }}></i>
          Shop-Floor QC Check
        </h3>
        <p style={{ margin: "4px 0 0 0", color: "#c7d2fe", fontSize: "14px" }}>Scan barcodes to perform random quality checks.</p>
      </div>

      <div style={{ padding: "40px 20px", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
        <div style={{ width: "100%", maxWidth: "500px" }}>
          
          {/* SCANNER INPUT BOX */}
          <div style={{ background: "white", padding: "24px", borderRadius: "16px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }}>
            <form onSubmit={handleScan}>
              <label style={{ fontWeight: 600, color: "#334155", marginBottom: "8px", display: "block" }}>Scan Lot Barcode</label>
              <div style={{ display: "flex", gap: "10px" }}>
                <input 
                  type="text" 
                  value={barcode} 
                  onChange={(e) => setBarcode(e.target.value.toUpperCase())}
                  placeholder="e.g. NSP126MAR40SKWO..."
                  autoFocus
                  style={{ flex: 1, padding: "12px 16px", borderRadius: "8px", border: "2px solid #e2e8f0", fontSize: "16px", outline: "none", transition: "border-color 0.2s" }}
                  onFocus={e => e.target.style.borderColor = "#6366f1"}
                  onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                  disabled={loading || submitting}
                />
                <button type="submit" disabled={loading || submitting || !barcode.trim()} 
                  style={{ background: "#4f46e5", color: "white", border: "none", padding: "0 24px", borderRadius: "8px", fontWeight: 600, transition: "background 0.2s" }}
                  onMouseOver={e => e.currentTarget.style.background = "#4338ca"}
                  onMouseOut={e => e.currentTarget.style.background = "#4f46e5"}
                >
                  {loading ? <i className="bi bi-hourglass-split spin"></i> : 'Find'}
                </button>
              </div>
            </form>
          </div>

          {/* LOT DETAILS & ACTIONS */}
          {lotDetails && !showFailForm && (
            <div style={{ marginTop: "24px", background: "white", borderRadius: "16px", overflow: "hidden", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", animation: "fadeIn 0.3s ease-out" }}>
              <div style={{ padding: "20px", borderBottom: "1px solid #f1f5f9" }}>
                <h5 style={{ margin: 0, color: "#1e293b", fontWeight: 700 }}>Lot Details</h5>
              </div>
              <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
                
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", background: "#f8fafc", borderRadius: "8px" }}>
                  <span style={{ color: "#64748b", fontSize: "14px", fontWeight: 500 }}>Lot ID</span>
                  <span style={{ color: "#0f172a", fontWeight: 700, fontSize: "15px" }}>{lotDetails.lot_id}</span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", background: "#f8fafc", borderRadius: "8px" }}>
                  <span style={{ color: "#64748b", fontSize: "14px", fontWeight: 500 }}>Current Department</span>
                  <span style={{ color: "#0ea5e9", fontWeight: 700, fontSize: "15px" }}>{lotDetails.process_name || 'N/A'}</span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", background: "#f8fafc", borderRadius: "8px" }}>
                  <span style={{ color: "#64748b", fontSize: "14px", fontWeight: 500 }}>Expected Quantity</span>
                  <span style={{ color: "#0f172a", fontWeight: 700, fontSize: "15px" }}>{lotDetails.expected_qty || '-'}</span>
                </div>

                {lotDetails.status?.includes('Blocked') && (
                  <div style={{ padding: "12px", background: "#fef2f2", color: "#b91c1c", borderRadius: "8px", fontSize: "14px", fontWeight: 500, textAlign: "center" }}>
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    This lot is currently Blocked.
                  </div>
                )}
                
                {/* ACTIONS */}
                <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                  <button 
                    onClick={() => setShowFailForm(true)}
                    style={{ flex: 1, padding: "14px", background: "#fff", border: "2px solid #ef4444", color: "#ef4444", borderRadius: "10px", fontWeight: 700, fontSize: "15px", transition: "all 0.2s" }}
                  >
                    <i className="bi bi-x-circle me-2"></i> QC Fail
                  </button>
                  <button 
                    onClick={() => submitQC('PASS')}
                    disabled={submitting}
                    style={{ flex: 1, padding: "14px", background: "#10b981", border: "none", color: "white", borderRadius: "10px", fontWeight: 700, fontSize: "15px", transition: "all 0.2s", boxShadow: "0 4px 6px -1px rgba(16, 185, 129, 0.4)" }}
                  >
                    <i className="bi bi-check-circle me-2"></i> Pass QC
                  </button>
                </div>

              </div>
            </div>
          )}

          {/* FAIL FORM */}
          {showFailForm && (
            <div style={{ marginTop: "24px", background: "white", borderRadius: "16px", overflow: "hidden", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", animation: "fadeIn 0.3s ease-out" }}>
              <div style={{ padding: "20px", background: "#fef2f2", borderBottom: "1px solid #fca5a5" }}>
                <h5 style={{ margin: 0, color: "#b91c1c", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="bi bi-exclamation-octagon-fill"></i> Report Defect
                </h5>
              </div>
              <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
                
                <div>
                  <label style={{ fontWeight: 600, color: "#334155", marginBottom: "8px", display: "block", fontSize: "14px" }}>Defect Category <span className="text-danger">*</span></label>
                  <select 
                    value={defectCategory}
                    onChange={e => setDefectCategory(e.target.value)}
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none" }}
                  >
                    <option value="">Select Category</option>
                    <option value="Torn/Hole">Torn / Hole</option>
                    <option value="Stains/Spots">Stains / Spots</option>
                    <option value="Stitching Issue">Stitching Issue</option>
                    <option value="Size Variance">Size Variance</option>
                    <option value="Wrong Material">Wrong Material</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontWeight: 600, color: "#334155", marginBottom: "8px", display: "block", fontSize: "14px" }}>Actual Checked Quantity <span className="text-danger">*</span></label>
                  <input 
                    type="number"
                    value={actualQty}
                    onChange={e => setActualQty(e.target.value)}
                    placeholder={`Expected: ${lotDetails?.expected_qty || '-'}`}
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none" }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: 600, color: "#334155", marginBottom: "8px", display: "block", fontSize: "14px" }}>Photo Evidence <span className="text-danger">*</span></label>
                  
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
                    {photos.map((file, idx) => (
                      <div key={idx} style={{ position: "relative", width: "70px", height: "70px" }}>
                        <img src={URL.createObjectURL(file)} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "8px" }} />
                        <button 
                          onClick={() => setPhotos(photos.filter((_, i) => i !== idx))}
                          style={{ position: "absolute", top: -5, right: -5, background: "#ef4444", color: "white", border: "none", borderRadius: "50%", width: 20, height: 20, fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                        >✕</button>
                      </div>
                    ))}
                    
                    <button 
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      style={{ width: "70px", height: "70px", background: "#f1f5f9", border: "2px dashed #cbd5e1", borderRadius: "8px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#64748b", cursor: "pointer" }}
                    >
                      <i className="bi bi-camera" style={{ fontSize: "20px" }}></i>
                    </button>
                  </div>
                  
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={e => {
                      if (e.target.files?.length) {
                        setPhotos([...photos, ...Array.from(e.target.files)]);
                      }
                      e.target.value = null; // reset
                    }}
                    accept="image/*" 
                    capture="environment"
                    multiple
                    style={{ display: "none" }} 
                  />
                  <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0 }}>Upload clear photos of the defect.</p>
                </div>

                <div>
                  <label style={{ fontWeight: 600, color: "#334155", marginBottom: "8px", display: "block", fontSize: "14px" }}>Comments</label>
                  <textarea 
                    value={comments}
                    onChange={e => setComments(e.target.value)}
                    placeholder="Describe the issue..."
                    rows={3}
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", resize: "none" }}
                  />
                </div>

                <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                  <button 
                    onClick={() => setShowFailForm(false)}
                    style={{ flex: 1, padding: "14px", background: "#f1f5f9", border: "none", color: "#475569", borderRadius: "10px", fontWeight: 600, fontSize: "15px" }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={() => submitQC('FAIL')}
                    disabled={submitting}
                    style={{ flex: 2, padding: "14px", background: "#ef4444", border: "none", color: "white", borderRadius: "10px", fontWeight: 700, fontSize: "15px", boxShadow: "0 4px 6px -1px rgba(239, 68, 68, 0.4)" }}
                  >
                    {submitting ? 'Raising Ticket...' : 'Raise Ticket'}
                  </button>
                </div>

              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
